import { createSlice } from "@reduxjs/toolkit";
import langs from '../../Pages/library.json';

let allowedLanguages = process.env.REACT_APP_ALLOWED_LANGUAGES ? process.env.REACT_APP_ALLOWED_LANGUAGES.split(',') : []
allowedLanguages = allowedLanguages.filter(value => Object.keys(langs).includes(value))
if (allowedLanguages.length == 0) {
    allowedLanguages = Object.keys(langs)
}
const defaultLanguage = process.env.REACT_APP_DEFAULT_LANGUAGE && allowedLanguages.includes(process.env.REACT_APP_DEFAULT_LANGUAGE) ?
    process.env.REACT_APP_DEFAULT_LANGUAGE :
    allowedLanguages[0]

const initialState = {
    activeVessel: '',
    heart_rate: false,
    lv_myocardium_mass: false,
    radii: false,
    bifurcations: false,
    overlayStatus: false,
    vesselJSON: false,
    uploadedVessels: {},
    files: {},
    studyInfo: {
        ref_id: "",
        patient_id: "",
        report_date: "",
        ct_study_date: "",
        institution: "",
        study_description: "",
    },
    view: [
        {
            id: 1,
            name: "3dcoronarymodel",
            status: true,
            title: "3D coronary model",
        },
        {
            id: 2,
            name: "leftanteriorFFR",
            status: true,
            title: "FFR",
        },
        {
            id: 3,
            name: "leftcircumflexFFR",
            status: false,
            title: "FFR",
        },
        {
            id: 4,
            name: "rightcoronaryFFR",
            status: false,
            title: "FFR",
        },
        {
            id: 5,
            name: "rightcoronaryFFR",
            status: false,
            title: "FFR",
        },
        {
            id: 6,
            name: "ffrpilot",
            status: true,
            title: "FFR pilot",
        },
        {
            id: 7,
            name: "diameterpilot",
            status: true,
            title: "Diameter pilot",
        },
        {
            id: 8,
            name: "straightened",
            status: true,
            title: "Straightened vessel",
        },
        {
            id: 9,
            name: "crossections",
            status: true,
            title: "Crossections",
        },
    ],
    handlerImages: {
        lad: [],
        lcx: [],
        rca: [],
    },
    printCamera: {
        lad: {
            position: { x: false, y: false, z: false },
            rotation: { x: false, y: false, z: false }
        },
        lcx: {
            position: { x: false, y: false, z: false },
            rotation: { x: false, y: false, z: false }
        },
        rca: {
            position: { x: false, y: false, z: false },
            rotation: { x: false, y: false, z: false }
        }
    },
    viewFFR: {
        lad: { ffr: false, x: false },
        lcx: { ffr: false, x: false },
        rca: { ffr: false, x: false }
    },
    printAbout: {
        lad: { top: false, left: false },
        lcx: { top: false, left: false },
        rca: { top: false, left: false }
    },
    isFFRCalculated: false,
    allowedLanguages: allowedLanguages,
    printLang: defaultLanguage,
    supplementary: {
        cfd: {
            viscosity: 0.046,
            density: 1.06,
            p0: 4500,
            windkessel_c: 8.76e-06,
            windkessel_r1: 3316,
            windkessel_r2: "",
            elasticity_k1: 2000000,
            elasticity_k2: -22.53,
            elasticity_k3: 865000,
            gradient_version: 1,
            hyperemia: 1,
            stenosis_mode: 2,
            turbulence_term: 0.152,
            q_function_type: 0,
            use_fake_values: 0,
            velocity_profile: 0,
            viscous_coefficient_1: 0.43,
            viscous_coefficient_2: 0.64
        },
        pinn: {
            age: 60,
            gender: 1,
            height: 175,
            weight: 68,
            tobacco_abuse: "",
            diabetes: "",
            hypertension: "",
            previous_mi_history: "",
            previous_pci: "",
            viscosity: 0.05,
            density: 1.1,
            p0: "",
            windkessel_c: "",
            windkessel_r1: "",
            windkessel_r2: "",
            elasticity: "",
            hyperemia: 1,
            myocardium_mass_multiplier: 3.5,
            murrey_exponent: 2.27
        }
    },
    modelType: "pinn"
};

const flowviewer = createSlice({
    name: "flowviewer",
    initialState: initialState,
    reducers: {
        reset() {
           return initialState;
        },
        setOverlayStatus(state, action) {
            state.overlayStatus = action.payload;
        },
        setFFRCalculationStatus(state, action) {
            state.isFFRCalculated = action.payload;
        },
        setHeartRate(state, action) {
            state.heart_rate = action.payload;
        },
        setRadii(state, action) {
            state.radii = action.payload;
        },
        setBifurcations(state, action) {
            state.bifurcations = action.payload;
        },
        setLvMyocardiumMass(state, action) {
            state.lv_myocardium_mass = action.payload;
        },
        setSupplementary(state, action) {
            if (action.payload.modelType in state.supplementary) {
                if (action.payload.name in state.supplementary[action.payload.modelType]) {
                    state.supplementary[action.payload.modelType][action.payload.name] = action.payload.value
                }
            }
        },
        loadVessels(state, action) {
            state.activeVessel = "";
            state.uploadedVessels = {};
            action.payload.forEach(vessel => {
                state.uploadedVessels[vessel] = {
                    center_line: false,
                    ffr: false
                }
            });
        },
        setVesselInfo(state, action) {
            const vessel = action.payload.vessel;
            if (vessel in state.uploadedVessels) {
                state.uploadedVessels[vessel] = {
                    ...state.uploadedVessels[vessel],
                    ...action.payload.info
                };
            }
        },
        setActiveVessel(state, action) {
            state.activeVessel = action.payload;
        },
        setPrintAbout(state, action) {
            const type = action.payload.type;
            const data = action.payload.data;
            // eslint-disable-next-line default-case
            switch (type) {
                case "lad":
                    state.printAbout.lad.top = data.top;
                    state.printAbout.lad.left = data.left;
                    break;
                case "lcx":
                    state.printAbout.lcx.top = data.top;
                    state.printAbout.lcx.left = data.left;
                    break;
                case "rca":
                    state.printAbout.rca.top = data.top;
                    state.printAbout.rca.left = data.left;
                    break;
            }
        },
        setFFR(state, action) {
            if (action.payload.vessel) {
                state.viewFFR[action.payload.vessel].ffr = action.payload.ffr;
                state.viewFFR[action.payload.vessel].x = action.payload.x;
            }
        },
        setPrintCamera(state, action) {
            const type = action.payload.type ? action.payload.type: state.activeVessel;
            const data = action.payload.data;
            // eslint-disable-next-line default-case
            switch (type) {
                case "lad":
                    state.printCamera.lad.position.x = data.position.x;
                    state.printCamera.lad.position.y = data.position.y;
                    state.printCamera.lad.position.z = data.position.z;
                    state.printCamera.lad.rotation.x = data.rotation.x;
                    state.printCamera.lad.rotation.y = data.rotation.y;
                    state.printCamera.lad.rotation.z = data.rotation.z;
                    break;
                case "lcx":
                    state.printCamera.lcx.position.x = data.position.x;
                    state.printCamera.lcx.position.y = data.position.y;
                    state.printCamera.lcx.position.z = data.position.z;
                    state.printCamera.lcx.rotation.x = data.rotation.x;
                    state.printCamera.lcx.rotation.y = data.rotation.y;
                    state.printCamera.lcx.rotation.z = data.rotation.z;
                    break;
                case "rca":
                    state.printCamera.rca.position.x = data.position.x;
                    state.printCamera.rca.position.y = data.position.y;
                    state.printCamera.rca.position.z = data.position.z;
                    state.printCamera.rca.rotation.x = data.rotation.x;
                    state.printCamera.rca.rotation.y = data.rotation.y;
                    state.printCamera.rca.rotation.z = data.rotation.z;
                    break;
            }
        },
        setHandlerImage(state, action) {
            const id = action.payload.id;
            const type = action.payload.type;
            const segment = action.payload.segment;
            let find;
            switch (type) {
                case "lad":
                    find = state.handlerImages.lad.find(item => item.id === id);
                    if (find) {
                        state.handlerImages.lad = state.handlerImages.lad.filter(item => item.id !== id);
                    } else {
                        state.handlerImages.lad.push({ id: id, segment })
                    }
                    break;
                case "lcx":
                    find = state.handlerImages.lcx.find(item => item.id === id);
                    if (find) {
                        state.handlerImages.lcx = state.handlerImages.lcx.filter(item => item.id !== id);
                    } else {
                        state.handlerImages.lcx.push({ id: id, segment })
                    }
                    break;
                case "rca":
                    find = state.handlerImages.rca.find(item => item.id === id);
                    if (find) {
                        state.handlerImages.rca = state.handlerImages.rca.filter(item => item.id !== id);
                    } else {
                        state.handlerImages.rca.push({ id: id, segment })
                    }
                    break;
            }
        },
        setVesselJson(state, action) {
            state.vesselJSON = action.payload;
        },
        setView(state, action) {
            const id = action.payload.id;
            const status = action.payload.status;
            const find = state.view.find(item => item.id === id);
            find.status = status;
        },
        setFon(state, action) {
            state.fon = action.payload;
        },
        setPrintLang(state, action) {
            state.printLang = action.payload
        },
        setModelType(state, action) {
            state.modelType = (action.payload == "cfd") ? "cfd" : "pinn"
        },
        setFile(state, action) {
            state.files[action.payload.name] = action.payload.link;
        },
        setStudyInfo(state, action) {
            state.studyInfo = { ...state.studyInfo, ...action.payload };
        }
    }
});

export default flowviewer.reducer;
export const {
    reset,
    setActiveVessel,
    setHeartRate,
    setRadii,
    setBifurcations,
    setLvMyocardiumMass,
    setOverlayStatus,
    setPrintAbout,
    setPrintCamera,
    setHandlerImage,
    setSupplementary,
    setVesselInfo,
    setVesselJson,
    setView,
    setFFR,
    loadVessels,
    setFFRCalculationStatus,
    setPrintLang,
    setModelType,
    setFile,
    setStudyInfo
} = flowviewer.actions;
